import React, { useState, useRef, useEffect } from 'react';
import { Search, Package, Users, ShoppingCart, User, ArrowRightLeft, FileText, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useGlobalSearch, SearchResult } from '@/hooks/useGlobalSearch';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

interface GlobalSearchProps {
  className?: string;
  placeholder?: string;
  onResultSelect?: (result: SearchResult) => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  className,
  placeholder = "Buscar productos, clientes, ventas...",
  onResultSelect
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { results, isSearching, search, clearSearch } = useGlobalSearch();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (inputValue.trim()) {
        search(inputValue);
        setIsOpen(true);
      } else {
        clearSearch();
        setIsOpen(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [inputValue, search, clearSearch]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current && 
        !resultsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleResultClick = (result: SearchResult) => {
    setIsOpen(false);
    setInputValue('');
    clearSearch();
    
    if (onResultSelect) {
      onResultSelect(result);
      return;
    }

    // Default navigation behavior
    const navigateToResult = () => {
      switch (result.type) {
        case 'product':
          navigate('/products');
          break;
        case 'customer':
          navigate('/customers');
          break;
        case 'sale':
          navigate('/sales');
          break;
        case 'user':
          navigate('/users');
          break;
        case 'transfer':
          navigate('/transfers');
          break;
        default:
          break;
      }
    };

    navigateToResult();
  };

  const getResultIcon = (type: SearchResult['type']) => {
    const iconProps = { className: "h-4 w-4" };
    
    switch (type) {
      case 'product':
        return <Package {...iconProps} className="h-4 w-4 text-status-info" />;
      case 'customer':
        return <Users {...iconProps} className="h-4 w-4 text-status-success" />;
      case 'sale':
        return <ShoppingCart {...iconProps} className="h-4 w-4 text-warning" />;
      case 'user':
        return <User {...iconProps} className="h-4 w-4 text-primary" />;
      case 'transfer':
        return <ArrowRightLeft {...iconProps} className="h-4 w-4 text-accent-foreground" />;
      case 'invoice':
        return <FileText {...iconProps} className="h-4 w-4 text-status-error" />;
      default:
        return <Search {...iconProps} />;
    }
  };

  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'product': return 'Producto';
      case 'customer': return 'Cliente';
      case 'sale': return 'Venta';
      case 'user': return 'Usuario';
      case 'transfer': return 'Transferencia';
      case 'invoice': return 'Factura';
      default: return 'Resultado';
    }
  };

  const getTypeBadgeColor = (type: SearchResult['type']) => {
    switch (type) {
      case 'product': return 'bg-status-info-bg text-status-info border-status-info-border';
      case 'customer': return 'bg-status-success-bg text-status-success border-status-success-border';
      case 'sale': return 'bg-status-warning-bg text-warning border-status-warning-border';
      case 'user': return 'bg-primary-light text-primary border-border';
      case 'transfer': return 'bg-accent text-accent-foreground border-border';
      case 'invoice': return 'bg-status-error-bg text-status-error border-status-error-border';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className={cn("relative", className)}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          className="pl-9 bg-background/50 border-border/30"
          onFocus={() => {
            if (results.length > 0) {
              setIsOpen(true);
            }
          }}
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Search Results */}
      {isOpen && (results.length > 0 || isSearching) && (
        <div
          ref={resultsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-background/95 backdrop-blur-xl border border-border/30 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
        >
          {isSearching && results.length === 0 && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Buscando...
            </div>
          )}

          {results.length > 0 && (
            <div className="p-2">
              <div className="text-xs text-muted-foreground px-3 py-2 font-medium">
                {results.length} resultado{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}
              </div>
              
              {results.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}-${index}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-accent/50 transition-colors text-left group"
                >
                  <div className="flex-shrink-0">
                    {getResultIcon(result.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                        {result.title}
                      </p>
                      <Badge className={cn("text-xs px-1.5 py-0.5", getTypeBadgeColor(result.type))}>
                        {getTypeLabel(result.type)}
                      </Badge>
                    </div>
                    
                    {result.subtitle && (
                      <p className="text-xs text-muted-foreground truncate">
                        {result.subtitle}
                      </p>
                    )}
                    
                    {result.description && (
                      <p className="text-xs text-muted-foreground truncate mt-1">
                        {result.description}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {!isSearching && results.length === 0 && inputValue.trim().length >= 2 && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Search className="h-5 w-5 mr-2" />
              No se encontraron resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
};